import React from 'react';
import { FormattedMessage } from 'react-intl';

import { messages } from '../shipping-grid/messages';

/**
 * Refund Status Type ID mappings
 * @typedef {object} RefundStatusType
 * @property {number} ELIGIBLE - Eligible for refund
 * @property {number} REQUEST_SCHEDULED - Request has been scheduled
 * @property {number} PENDING - Request is pending
 * @property {number} APPROVED - Request has been approved
 * @property {number} CANCELLED - Request has been cancelled
 * @property {number} REJECTED - Request has been rejected
 * @property {number} DISABLED - Refund is disabled
 */
export const REFUND_STATUS_TYPES = {
  ELIGIBLE: 1,
  REQUEST_SCHEDULED: 2,
  PENDING: 3,
  APPROVED: 4,
  CANCELLED: 5,
  REJECTED: 6,
  DISABLED: 7,
};

/**
 * Message descriptors for refund status types
 * Maps numeric status IDs to their corresponding message descriptors
 */
const REFUND_STATUS_TYPE_MESSAGES = {
  [REFUND_STATUS_TYPES.ELIGIBLE]: messages.refundStatusEligible,
  [REFUND_STATUS_TYPES.REQUEST_SCHEDULED]: messages.refundStatusRequestScheduled,
  [REFUND_STATUS_TYPES.PENDING]: messages.refundStatusPending,
  [REFUND_STATUS_TYPES.APPROVED]: messages.refundStatusApproved,
  [REFUND_STATUS_TYPES.CANCELLED]: messages.refundStatusExcluded,
  [REFUND_STATUS_TYPES.REJECTED]: messages.refundStatusRejected,
  [REFUND_STATUS_TYPES.DISABLED]: messages.refundStatusDisabled,
};

/**
 * Returns a formatted message component for the given refund status type ID
 * @param {number} statusTypeId - The refund status type ID (1-7)
 * @returns {React.ReactElement|string} FormattedMessage component or the raw ID if unknown
 */
export const getRefundStatusMessage = (statusTypeId) => {
  const messageDescriptor = REFUND_STATUS_TYPE_MESSAGES[statusTypeId];

  if (!messageDescriptor) {
    return String(statusTypeId);
  }

  return <FormattedMessage {...messageDescriptor} />;
};

/**
 * Returns the message descriptor for the given refund status type ID
 * Useful when you need just the descriptor without the React component
 * @param {number} statusTypeId - The refund status type ID (1-7)
 * @returns {object | null} Message descriptor object or null if unknown
 */
export const getRefundStatusMessageDescriptor = (statusTypeId) => {
  return REFUND_STATUS_TYPE_MESSAGES[statusTypeId] || null;
};

/**
 * Checks if a status type ID is valid
 * @param {number} statusTypeId - The status type ID to validate
 * @returns {boolean} True if the status type ID is valid
 */
export const isValidRefundStatusType = (statusTypeId) => {
  return Object.values(REFUND_STATUS_TYPES).includes(statusTypeId);
};

/**
 * Returns criteria information for refund status filter
 * Each criterion has a value (the status type ID) and displayName (formatted message)
 * Only includes status types 2, 3, 4, 5, 6 (excludes Eligible and Disabled)
 * @returns {Array<{value: number, displayName: React.ReactElement}>} Array of criteria for filter options
 */
export const getRefundStatusFilterCriteria = () => {
  const filteredStatusTypes = [
    REFUND_STATUS_TYPES.REQUEST_SCHEDULED, // 2
    REFUND_STATUS_TYPES.PENDING, // 3
    REFUND_STATUS_TYPES.APPROVED, // 4
    REFUND_STATUS_TYPES.CANCELLED, // 5
    REFUND_STATUS_TYPES.REJECTED, // 6
  ];

  return filteredStatusTypes.map((shipmentRefundStatusTypeId) => {
    const messageDescriptor = REFUND_STATUS_TYPE_MESSAGES[shipmentRefundStatusTypeId];
    return {
      value: shipmentRefundStatusTypeId,
      displayName: messageDescriptor ? <FormattedMessage {...messageDescriptor} /> : <span>{shipmentRefundStatusTypeId}</span>,
    };
  });
};
